import type { ProjectCompanyCode } from 'library/types';
import { PRODUCT_COMPANY_STATUS_TYPES, PRODUCT_COMPANY_STATUS_TYPE_ENUM } from 'enums';

export type ProjectCompanyCodeStatusCounts = Record<PRODUCT_COMPANY_STATUS_TYPE_ENUM, number>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const normalizeStatus = (status: unknown): PRODUCT_COMPANY_STATUS_TYPE_ENUM => {
  const normalizedStatus = Number(status) as PRODUCT_COMPANY_STATUS_TYPE_ENUM;

  return PRODUCT_COMPANY_STATUS_TYPES.includes(normalizedStatus)
    ? normalizedStatus
    : PRODUCT_COMPANY_STATUS_TYPE_ENUM.ACTIVE;
};

const normalizeCompanyCode = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export const normalizeProjectCompanyCodes = (value: unknown): ProjectCompanyCode[] => {
  if (!Array.isArray(value)) return [];

  const normalizedCodes = new Map<string, ProjectCompanyCode>();

  value.forEach((item) => {
    // Keeping this compatibility path makes stale responses safe while every outgoing payload
    // still uses the current object contract.
    const companyCode = normalizeCompanyCode(isRecord(item) ? item.companyCode : item);

    if (!companyCode) return;

    normalizedCodes.set(companyCode, {
      companyCode,
      status: isRecord(item) ? normalizeStatus(item.status) : PRODUCT_COMPANY_STATUS_TYPE_ENUM.ACTIVE,
    });
  });

  return Array.from(normalizedCodes.values());
};

export const getProjectCompanyCodeValues = (value: unknown): string[] =>
  normalizeProjectCompanyCodes(value).map(({ companyCode }) => companyCode);

export const formatProjectCompanyCodes = (
  value: unknown,
  getStatusLabel: (status: PRODUCT_COMPANY_STATUS_TYPE_ENUM) => string
): string =>
  normalizeProjectCompanyCodes(value)
    .map(({ companyCode, status }) => `${companyCode} (${getStatusLabel(status)})`)
    .join(', ');

export const createEmptyProjectCompanyCodeStatusCounts = (): ProjectCompanyCodeStatusCounts => ({
  [PRODUCT_COMPANY_STATUS_TYPE_ENUM.PASSIVE]: 0,
  [PRODUCT_COMPANY_STATUS_TYPE_ENUM.ACTIVE]: 0,
  [PRODUCT_COMPANY_STATUS_TYPE_ENUM.TO_BE_INTEGRATED]: 0,
  [PRODUCT_COMPANY_STATUS_TYPE_ENUM.NOT_IN_CONTRACT]: 0,
});

export const getProjectCompanyCodeStatusCounts = (value: unknown): ProjectCompanyCodeStatusCounts =>
  normalizeProjectCompanyCodes(value).reduce<ProjectCompanyCodeStatusCounts>((counts, { status }) => {
    counts[status] += 1;
    return counts;
  }, createEmptyProjectCompanyCodeStatusCounts());

export const getProjectCompanyCodeStatus = (
  value: unknown,
  companyCode: string
): PRODUCT_COMPANY_STATUS_TYPE_ENUM | undefined =>
  normalizeProjectCompanyCodes(value).find((item) => item.companyCode === companyCode)?.status;
